import { CheckIcon } from "@heroicons/react/24/outline";
import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";
import { useState, useRef, useCallback, type KeyboardEvent } from "react";
import {
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { focusNextTabbable } from "./focus-next-tabbable";

type Props<T extends string> = {
  id: string;
  selectedValue: T;
  onSelectedValueChange: (value: T) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  items: { value: T; label: string }[];
  renderOption?: (option: { value: T; label: string }) => React.ReactNode;
  isLoading?: boolean;
  emptyMessage?: string;
  label?: string;
  hasContent?: boolean;
  error?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
};

export const AutoCompleteBorderless = React.forwardRef<
  HTMLInputElement,
  Props<string>
>(
  (
    {
      id,
      selectedValue,
      onSelectedValueChange,
      searchValue,
      onSearchValueChange,
      items,
      renderOption,
      isLoading,
      emptyMessage = "No items.",
      label,
      hasContent,
      error,
      required,
      className,
      disabled,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setOpen] = useState(false);
    const [hasFocused, setHasFocused] = useState(false);
    const [highlightedValue, setHighlightedValue] = useState("");
    const isSelectedValueInactive = Boolean(selectedValue && !hasFocused);

    // Combine the forwarded ref with our internal ref
    React.useImperativeHandle(ref, () => inputRef.current!, []);

    React.useEffect(() => {
      const selectedItem = items.find((item) => item.value === selectedValue);
      if (selectedItem && !searchValue && !hasFocused) {
        onSearchValueChange(selectedItem.label);
      }
    }, [selectedValue, items, searchValue, onSearchValueChange, hasFocused]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (!input) return;

        if (event.key === "Tab") return;

        if (!isOpen) {
          setOpen(true);
        }

        if (event.key === "Enter" && input.value !== "") {
          const selectedItem = items.find((item) => item.label === input.value);
          if (selectedItem) {
            onSelectedValueChange(selectedItem.value);
            onSearchValueChange(selectedItem.label);
            setOpen(false);
            setHasFocused(false);
          }
        }

        if (event.key === "Escape") {
          input.blur();
        }
      },
      [isOpen, items, onSelectedValueChange, onSearchValueChange],
    );

    const handleInputKeyDownCapture = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Tab") {
          event.preventDefault();
          event.stopPropagation();
          focusNextTabbable(event.currentTarget, event.shiftKey);
        }
      },
      [],
    );

    const handleFocus = useCallback(() => {
      if (disabled) return;
      setOpen(true);
      setHasFocused(true);
      onSearchValueChange("");
    }, [onSearchValueChange, disabled]);

    const handleInputClick = useCallback(() => {
      if (disabled || hasFocused) return;
      setOpen(true);
      setHasFocused(true);
      onSearchValueChange("");
    }, [disabled, hasFocused, onSearchValueChange]);

    const handleBlur = useCallback(() => {
      setOpen(false);
      setHasFocused(false);
      const selectedItem = items.find((item) => item.value === selectedValue);
      if (selectedItem) {
        onSearchValueChange(selectedItem.label);
      } else {
        // Clear input text if no value is selected
        onSearchValueChange("");
      }
    }, [selectedValue, items, onSearchValueChange]);

    const handleSelectOption = useCallback(
      (value: string) => {
        const selectedItem = items.find((item) => item.label === value);
        if (selectedItem?.value === selectedValue) {
          // remove the selected item
          onSelectedValueChange("" as string);
          onSearchValueChange("");
          setOpen(false);
          setHasFocused(false);
        } else if (selectedItem) {
          onSelectedValueChange(selectedItem.value);
          onSearchValueChange(selectedItem.label);
          setOpen(false);
          setHasFocused(false);
        }
      },
      [items, onSelectedValueChange, onSearchValueChange, selectedValue],
    );

    const placeholderWithAsterisk = required ? `${label} *` : label;

    return (
      <div className="relative">
        {hasContent && (
          <Typography
            variant="subtitleW3"
            className={cn(
              "absolute left-4 top-2 z-20 text-tertiary-text",
              error && "text-destructive",
            )}
          >
            {placeholderWithAsterisk}
          </Typography>
        )}
        <CommandPrimitive
          onKeyDown={handleKeyDown}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
          onMouseLeave={() => setHighlightedValue("")}
        >
          <div>
            <CommandPrimitive.Input
              id={id}
              ref={inputRef}
              value={searchValue}
              onValueChange={onSearchValueChange}
              onBlur={handleBlur}
              autoFocus={false}
              onFocus={handleFocus}
              onClick={handleInputClick}
              onKeyDownCapture={handleInputKeyDownCapture}
              placeholder={placeholderWithAsterisk}
              required={required}
              className={cn(
                "flex h-14 w-full bg-transparent px-4 text-lg outline-none",
                {
                  "caret-transparent": isSelectedValueInactive,
                },
                "placeholder:text-lg placeholder:text-tertiary-text",
                "disabled:cursor-not-allowed disabled:text-tertiary-text",
                "focus:outline-none",
                hasContent && "pt-6",
                className,
              )}
              disabled={disabled}
            />
          </div>
          <div className="relative mt-1">
            <div
              className={cn(
                "absolute top-0 z-50 w-full rounded-xl bg-white shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] outline-none",
                isOpen && !disabled ? "block" : "hidden",
              )}
            >
              <div className="rounded-xl ring-1 ring-slate-200">
                <div className="scrollbar-hidden max-h-[170px] overflow-y-auto">
                  <CommandList>
                    {isLoading ? (
                      <CommandPrimitive.Loading>
                        <div className="p-1">
                          <Skeleton className="h-8 w-full" />
                        </div>
                      </CommandPrimitive.Loading>
                    ) : null}
                    {items.length > 0 && !isLoading ? (
                      <CommandGroup>
                        {items.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.label}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onSelect={() => handleSelectOption(option.label)}
                            className="flex w-full items-center gap-2"
                          >
                            <CheckIcon
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedValue === option.value
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            {renderOption ? renderOption(option) : option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ) : null}
                    {!isLoading && items.length === 0 ? (
                      <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                        {emptyMessage}
                      </CommandPrimitive.Empty>
                    ) : null}
                  </CommandList>
                </div>
              </div>
            </div>
          </div>
        </CommandPrimitive>
      </div>
    );
  },
);

AutoCompleteBorderless.displayName = "AutoCompleteBorderless";
